<template>
  <div class="flex flex-col gap-4">
    <div>
      <label class="block text-sm mb-1 secondary_color text-[13px]">Choose associated object</label>
      <a-select
        v-model:value="selectedObjectId"
        class="w-full"
        placeholder="Select an associated object"
        :options="objectDropdownOptions"
        :loading="loadingObjects"
        :disabled="disabled"
        @change="handleObjectChange"
      >
        <template #suffixIcon>
          <ThemeImage
            :icon="imageNames.BOTTOM_ARROW"
            alt="dropdown"
            class="size-4"
          />
        </template>
      </a-select>
    </div>

    <div v-if="selectedObjectId">
      <label class="block text-sm mb-1 secondary_color text-[13px]">Choose property to sync</label>
      <a-select
        v-model:value="selectedAttributeId"
        class="w-full"
        placeholder="Select a property to sync"
        :options="attributeOptions"
        :loading="loadingAttributes"
        :disabled="disabled"
        show-search
        :filter-option="filterOption"
        @change="handleAttributeChange"
      >
        <template #suffixIcon>
          <ThemeImage
            :icon="imageNames.BOTTOM_ARROW"
            alt="dropdown"
            class="size-4"
          />
        </template>
        <template #option="{ label, type }">
          <div class="flex items-center justify-between w-full">
            <span>{{ label }}</span>
            <span class="text-xs secondary_color">{{ type }}</span>
          </div>
        </template>
      </a-select>
    </div>

    <div v-if="selectedAttributeId && showRecordSelection">
      <label class="block text-sm mb-1 secondary_color text-[13px]">Choose which {{ selectedObject?.name?.toLowerCase() }} to sync</label>
      <a-select
        v-model:value="selectedRecordSelection"
        class="w-full"
        :placeholder="`Select which ${selectedObject?.name?.toLowerCase() || 'record'} to sync`"
        :options="recordSelectionOptions"
        :disabled="disabled"
        @change="handleRecordSelectionChange"
      >
        <template #suffixIcon>
          <ThemeImage
            :icon="imageNames.BOTTOM_ARROW"
            alt="dropdown"
            class="size-4"
          />
        </template>
      </a-select>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import CRMService from "@/src/api/CRMService.js";
import { useStore } from "vuex";
import { getServiceForSlug } from "@/src/constants/crmConstants.js";

const store = useStore();

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      from_object_id: null,
      from_relationship_id: null,
      from_attribute_id: null,
      record_selection: null,
    }),
  },
  entityType: {
    type: String,
    required: true,
  },
  service: {
    type: String,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue"]);

const loadingObjects = ref(false);
const loadingAttributes = ref(false);
const relatedObjects = ref([]);
const relationships = ref([]);
const sourceObjectSchema = ref(null);

const selectedObjectId = ref(null);
const selectedAttributeId = ref(null);
const selectedRecordSelection = ref(null);

const recordSelectionOptions = [
  { label: "First Created", value: "first_created" },
  { label: "Most Recently Created", value: "most_recent" },
];

const selectedObject = computed(() => {
  return relatedObjects.value.find((obj) => obj.id === selectedObjectId.value) || null;
});

const objectDropdownOptions = computed(() => {
  return relatedObjects.value.map((obj) => ({
    value: obj.id,
    label: obj.plural_name || obj.name,
  }));
});

const selectedRelationship = computed(() => {
  if (!selectedObject.value) return null;

  return relationships.value.find((rel) => {
    const sourceSlug = rel.source_object?.slug || rel.source_object_id;
    const targetSlug = rel.target_object?.slug || rel.target_object_id;
    return sourceSlug === selectedObject.value.slug || targetSlug === selectedObject.value.slug;
  });
});

const showRecordSelection = computed(() => {
  if (!selectedRelationship.value) return false;

  const { cardinality } = selectedRelationship.value;
  if (!cardinality) return false;

  const sourceSlug = selectedRelationship.value.source_object?.slug || selectedRelationship.value.source_object_id;
  const isCurrentEntitySource = sourceSlug === props.entityType;
  const parts = cardinality.split("_to_");
  if (parts.length !== 2) return false;
  
  const [sourceCardinality, targetCardinality] = parts;

  if (isCurrentEntitySource) {
    return targetCardinality === "many";
  } else {
    return sourceCardinality === "many";
  }
});

const attributeOptions = computed(() => {
  if (!sourceObjectSchema.value?.attributes) return [];

  return sourceObjectSchema.value.attributes
    .filter((attr) => {
      const type = (attr.type || "").toLowerCase();
      return type !== "property_sync" && type !== "relationship";
    })
    .map((attr) => ({
      value: attr.id,
      label: attr.name,
      type: attr.type,
      slug: attr.slug,
    }));
});

const filterOption = (input, option) => {
  return option.label.toLowerCase().includes(input.toLowerCase());
};

const fetchRelatedObjects = async () => {
  if (!props.entityType) return;

  loadingObjects.value = true;
  try {
    const [objectsResponse, schemaResponse] = await Promise.all([
      CRMService.fetchDynamicCRMListObjects(),
      CRMService.fetchSchemaObject(props.entityType, props.service),
    ]);

    const objects = objectsResponse.success ? (objectsResponse.objects || []) : [];
    const schemaRelationships = schemaResponse.success ? (schemaResponse.schema?.relationships || []) : [];
    relationships.value = schemaRelationships;

    const relatedSlugs = new Set();
    schemaRelationships.forEach((rel) => {
      const sourceSlug = rel.source_object?.slug || rel.source_object_id;
      const targetSlug = rel.target_object?.slug || rel.target_object_id;
      if (sourceSlug && sourceSlug !== props.entityType) {
        relatedSlugs.add(sourceSlug);
      }
      if (targetSlug && targetSlug !== props.entityType) {
        relatedSlugs.add(targetSlug);
      }
    });

    relatedObjects.value = objects.filter((obj) => relatedSlugs.has(obj.slug));
  } catch (error) {
    console.error("Error fetching related objects:", error);
    relatedObjects.value = [];
  } finally {
    loadingObjects.value = false;
  }
};

const fetchSourceObjectSchema = async (objectSlug) => {
  if (!objectSlug) return;

  loadingAttributes.value = true;
  try {
    // Source object may belong to a different service than the current entity,
    // so resolve per-slug instead of reusing props.service.
    const sourceService = getServiceForSlug(objectSlug, store);
    const response = await CRMService.fetchSchemaObject(objectSlug, sourceService);
    if (response.success && response.schema) {
      sourceObjectSchema.value = response.schema;
    }
  } catch (error) {
    console.error("Error fetching source object schema:", error);
  } finally {
    loadingAttributes.value = false;
  }
};

const emitUpdate = () => {
  const config = {
    from_object_id: selectedObjectId.value,
    from_relationship_id: selectedRelationship.value?.id || null,
    from_attribute_id: selectedAttributeId.value,
    record_selection: showRecordSelection.value ? selectedRecordSelection.value : null,
  };
  emit("update:modelValue", config);
};

const handleObjectChange = () => {
  selectedAttributeId.value = null;
  selectedRecordSelection.value = null;
  sourceObjectSchema.value = null;

  if (selectedObject.value?.slug) {
    fetchSourceObjectSchema(selectedObject.value.slug);
  }
  emitUpdate();
};

const handleAttributeChange = () => {
  if (showRecordSelection.value && !selectedRecordSelection.value) {
    selectedRecordSelection.value = "most_recent";
  }
  emitUpdate();
};

const handleRecordSelectionChange = () => {
  emitUpdate();
};

const initializeFromModelValue = async () => {
  if (props.modelValue?.from_object_id) {
    selectedObjectId.value = props.modelValue.from_object_id;
    const obj = relatedObjects.value.find((o) => o.id === props.modelValue.from_object_id);
    if (obj?.slug) {
      await fetchSourceObjectSchema(obj.slug);
    }
  }
  if (props.modelValue?.from_attribute_id) {
    selectedAttributeId.value = props.modelValue.from_attribute_id;
  }
  if (props.modelValue?.record_selection) {
    selectedRecordSelection.value = props.modelValue.record_selection;
  }
};

watch(
  () => props.entityType,
  async (newVal) => {
    if (newVal) {
      await fetchRelatedObjects();
      if (props.modelValue?.from_object_id) {
        await initializeFromModelValue();
      }
    }
  },
  { immediate: true }
);

watch(
  () => props.modelValue,
  () => {
    if (props.modelValue && relatedObjects.value.length > 0) {
      initializeFromModelValue();
    }
  },
  { deep: true }
);
</script>
